import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, ArrowDownRight, Minus, Glasses } from "lucide-react";
import { api } from "../lib/api";
import { storeName } from "../lib/labels";
import LocalStorePicker from "./LocalStorePicker";
import ExportButtons from "./ExportButtons";
import { downloadCSV, downloadPDF, fmt } from "../lib/exports";
import { useSort } from "../lib/useSort";
import { SortHeader } from "./SortControls";
import PeriodBadge from "./PeriodBadge";

// Fixed column order for the per-store unit columns — matches the store
// picker layout on the rest of the dashboard.
const STORE_COLUMNS = [
  "Public Butter Kensington",
  "Black Market 347",
  "Black Market 256",
  "Dirty Mike's",
];

function money(v) {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}
function fullMoney(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function Delta({ pct, small = false }) {
  const size = small ? 10 : 12;
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-0.5 font-mono-bm text-[11px] text-neutral-400">
        <Minus size={size} strokeWidth={2} /> —
      </span>
    );
  }
  const up = pct >= 0;
  const cls = up ? "text-emerald-700" : "text-[#c72c2c]";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono-bm text-[11px] font-semibold ${cls}`}>
      <Icon size={size} strokeWidth={2.5} />
      {up ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

export default function EyewearPanel({ range, compareMode = "same_period" }) {
  const [outletIds, setOutletIds] = useState([]);
  // Store breakdown only makes sense when looking at every store at once.
  const showStoreCols = outletIds.length === 0;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!range) return;
    let cancel = false;
    setLoading(true);
    const params = { start_date: range.start, end_date: range.end };
    if (outletIds.length) params.outlet_ids = outletIds.join(",");
    if (compareMode === "same_dates") params.yoy_mode = "same_dates";
    api.get("/products/eyewear", { params })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range?.start, range?.end, outletIds.join(","), compareMode]);

  const h = data?.headline;
  const byOutlet = data?.by_outlet || [];
  const rows = data?.rows || [];

  // Build a name → outlet_id lookup so we can pull units for our fixed columns
  const outletIdByName = useMemo(() => {
    const m = {};
    for (const o of byOutlet) m[o.name] = o.outlet_id;
    return m;
  }, [byOutlet]);

  const yoyLabel = compareMode === "same_dates" ? "vs. same dates last year" : "vs. same period last year";

  const sort = useSort("revenue", "desc", {
    units: (r) => r.units,
    growth: (r) => r.unit_growth_pct,
    revenue: (r) => r.revenue,
  });
  const sortedRows = sort.apply(rows);

  const buildExport = () => {
    const columns = [
      { key: "n", label: "#", align: "right" },
      { key: "product", label: "Product" },
      { key: "sku", label: "SKU" },
      { key: "brand", label: "Brand" },
      { key: "category", label: "Category" },
      { key: "units", label: "Units", align: "right" },
      { key: "growth", label: "% Δ vs LY", align: "right" },
      { key: "revenue", label: "Revenue", align: "right" },
      ...STORE_COLUMNS.map((n) => ({ key: `col_${n}`, label: storeName(n), align: "right" })),
    ];
    const flat = sortedRows.map((r, i) => {
      const row = {
        n: i + 1,
        product: r.name,
        sku: r.sku || "",
        brand: r.brand || "",
        category: r.category || "",
        units: fmt.int(r.units),
        growth: r.unit_growth_pct != null ? fmt.pct1(r.unit_growth_pct) : "",
        revenue: fmt.money0(r.revenue),
      };
      for (const n of STORE_COLUMNS) {
        const oid = outletIdByName[n];
        row[`col_${n}`] = oid ? (r.units_by_outlet?.[oid] || 0) : 0;
      }
      return row;
    });
    return {
      filenameBase: `eyewear-${range?.label || "range"}`,
      meta: { title: "Eyewear & Sunglasses", subtitle: `SKU-level sell-through · ${range?.label || ""} · ${yoyLabel}` },
      columns,
      rows: flat,
    };
  };

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="eyewear-panel">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <div className="bm-eyebrow inline-flex items-center gap-1">
          <Glasses size={11} /> Table 6 · Eyewear & Sunglasses
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="bm-eyebrow text-neutral-500">{range?.label || ""}</div>
          <LocalStorePicker value={outletIds} onChange={setOutletIds} testId="eyewear-stores" />
        </div>
      </div>
      <h3 className="bm-headline text-3xl mb-1">Eyewear.</h3>
      <PeriodBadge range={range} className="mb-2" />
      <p className="bm-eyebrow text-neutral-500 mb-1">
        SKU-level sell-through across sunglasses & eyewear · {data?.matched_products || 0} SKUs in the catalog
      </p>
      <p className="bm-eyebrow text-neutral-400 mb-4">
        {yoyLabel} · {data?.yoy_start} → {data?.yoy_end}
      </p>

      {loading && !data && <div className="text-sm text-neutral-500">Loading…</div>}

      {h && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-3" data-testid="eyewear-headline-revenue">
            <div className="bm-eyebrow text-neutral-500">Revenue</div>
            <div className="text-xl font-semibold tabular-nums mt-1">{fullMoney(h.revenue)}</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="bm-eyebrow text-neutral-500">prior</span><Delta pct={h.revenue_delta_pct} small />
              <span className="bm-eyebrow text-neutral-500 ml-1">LY</span><Delta pct={h.yoy_revenue_delta_pct} small />
            </div>
          </div>
          <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-3" data-testid="eyewear-headline-units">
            <div className="bm-eyebrow text-neutral-500">Units</div>
            <div className="text-xl font-semibold tabular-nums mt-1">{(h.units || 0).toLocaleString()}</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="bm-eyebrow text-neutral-500">prior</span><Delta pct={h.units_delta_pct} small />
              <span className="bm-eyebrow text-neutral-500 ml-1">LY</span><Delta pct={h.yoy_units_delta_pct} small />
            </div>
          </div>
          <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-3" data-testid="eyewear-headline-txns">
            <div className="bm-eyebrow text-neutral-500">Transactions</div>
            <div className="text-xl font-semibold tabular-nums mt-1">{(h.transactions || 0).toLocaleString()}</div>
            <div className="bm-eyebrow text-neutral-500 mt-1">baskets w/ eyewear</div>
          </div>
          <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-3" data-testid="eyewear-headline-aov">
            <div className="bm-eyebrow text-neutral-500">Avg. ticket</div>
            <div className="text-xl font-semibold tabular-nums mt-1">{fullMoney(h.aov)}</div>
            <div className="bm-eyebrow text-neutral-500 mt-1">per basket</div>
          </div>
        </div>
      )}

      {byOutlet.length > 0 && (
        <div className="mb-6">
          <div className="bm-eyebrow text-neutral-500 mb-2">By store</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="eyewear-by-outlet">
            {byOutlet.map((o) => (
              <div key={o.outlet_id} className="border border-[#0a0a0a]/40 bg-[#f5f2eb] p-3">
                <div className="font-medium text-sm">{storeName(o.name)}</div>
                <div className="font-mono-bm tabular-nums text-lg mt-1">{fullMoney(o.revenue)}</div>
                <div className="bm-eyebrow text-neutral-500 font-mono-bm">
                  {(o.units || 0).toLocaleString()}u · {o.transactions}tx
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div className="bm-eyebrow text-neutral-500">
              SKU-level · sorted by {sort.sortKey === "growth" ? "Δ vs LY" : sort.sortKey} · {sort.sortDir} · {rows.length} SKUs
            </div>
            <ExportButtons
              disabled={!rows.length}
              testIdPrefix="eyewear-export"
              onCSV={() => { const e = buildExport(); downloadCSV(e.filenameBase, e.columns, e.rows); }}
              onPDF={() => { const e = buildExport(); downloadPDF(e.filenameBase, e.meta, e.columns, e.rows); }}
            />
          </div>
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto border border-[#0a0a0a]/20">
            <table className="w-full text-sm" data-testid="eyewear-table">
              <thead className="sticky top-0 bg-[#ede8dc]">
                <tr className="border-b border-[#0a0a0a]">
                  <th className="text-left bm-eyebrow text-neutral-600 py-2 pr-3 w-8">#</th>
                  <th className="text-left bm-eyebrow text-neutral-600 py-2 pr-3">Product · SKU</th>
                  <th className="text-right py-2 px-3 w-20"><SortHeader label="Ttl Units" k="units" state={sort} testId="eyewear-sort-units" /></th>
                  <th className="text-right py-2 px-3 w-20"><SortHeader label="% Δ vs LY" k="growth" state={sort} testId="eyewear-sort-growth" /></th>
                  <th className="text-right py-2 px-3 w-24"><SortHeader label="Ttl Revenue" k="revenue" state={sort} testId="eyewear-sort-revenue" /></th>
                  {showStoreCols && STORE_COLUMNS.map((name) => (
                    <th
                      key={name}
                      className="text-right bm-eyebrow text-neutral-600 py-2 px-3 w-16 hidden md:table-cell"
                      title={`${name} · units`}
                    >
                      {storeName(name)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, i) => {
                  const ubo = r.units_by_outlet || {};
                  return (
                    <tr key={r.product_id} className="border-b border-[#0a0a0a]/10 hover:bg-[#0a0a0a]/5" data-testid={`eyewear-row-${i}`}>
                      <td className="py-2 pr-3 font-mono-bm text-xs text-neutral-500 align-middle">{String(i + 1).padStart(2, "0")}</td>
                      <td className="py-2 pr-3 align-middle">
                        <div className="font-medium truncate max-w-[200px] md:max-w-[300px]" title={r.parent_name ? `${r.parent_name} · ${r.name}` : r.name}>{r.name}</div>
                        <div className="hidden md:block bm-eyebrow text-neutral-500 font-mono-bm">
                          {r.sku ? `SKU ${r.sku}` : "no sku"}{r.brand ? ` · ${r.brand}` : ""}{r.category ? ` · ${r.category}` : ""}
                        </div>
                      </td>
                      <td className={`py-2 px-3 text-right font-mono-bm tabular-nums ${sort.sortKey === "units" ? "font-bold" : "font-semibold"}`}>{r.units.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right"><Delta pct={r.unit_growth_pct} small /></td>
                      <td className={`py-2 px-3 text-right font-mono-bm tabular-nums ${sort.sortKey === "revenue" ? "font-bold" : ""}`}>{money(r.revenue)}</td>
                      {showStoreCols && STORE_COLUMNS.map((name) => {
                        const oid = outletIdByName[name];
                        const u = oid ? (ubo[oid] || 0) : 0;
                        return (
                          <td
                            key={name}
                            className={`py-2 px-3 text-right font-mono-bm tabular-nums hidden md:table-cell ${u ? "" : "text-neutral-300"}`}
                            data-testid={`eyewear-row-${i}-${storeName(name)}`}
                          >
                            {u ? u.toLocaleString() : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
